import * as React from 'react';

import {
  Select as BaseSelect,
  SelectRootSlotProps,
  SelectProps,
  SelectListboxSlotProps,
} from '@mui/base/Select';
import { Option as BaseOption, OptionProps } from '@mui/base/Option';
import s from './styles.scss';
import cx from 'classnames';
import { Icon, enums, utils as u } from 'edrolo-ui';

interface Option {
  value: string;
  label: string;
}

interface Props {
  placeholder?: string;
  name?: string;
  defaultValue?: Option['value'];
  value?: Option['value'];
  options: Option[];
  disabled?: boolean;
  onChange?:
    | ((
        event: React.MouseEvent | React.KeyboardEvent | React.FocusEvent | null,
        value: string | string[] | null
      ) => void)
    | undefined;
}

export const Select = React.forwardRef(
  (
    {
      placeholder,
      defaultValue,
      value,
      options,
      disabled,
      onChange,
      name,
    }: Props,
    ref: React.ForwardedRef<HTMLButtonElement>
  ) => {
    {
      /* TODO: add a unique id or name attribute to a form field. This is not strictly needed, but still recommended even if you have an autocomplete attribute on the same element.*/
    }

    return (
      <div className={s.select}>
        <MUISelect
          placeholder={<span className={s.placeholder}>{placeholder}</span>}
          defaultValue={defaultValue}
          disabled={disabled}
          value={value}
          onChange={onChange}
          ref={ref}
          name={name}
        >
          {options.map((option: Option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </MUISelect>
      </div>
    );
  }
);

const Option = React.forwardRef<HTMLLIElement, OptionProps<Option['value']>>(
  (props, ref) => {
    return (
      <BaseOption
        ref={ref}
        {...props}
        slotProps={{
          root: ({ selected, highlighted }) => ({
            className: cx(
              s.option,
              selected && s.selected,
              highlighted && s.highlighted
            ),
          }),
        }}
      />
    );
  }
);

const Button = React.forwardRef(function Button<
  TValue extends Option['value'],
  Multiple extends boolean
>(
  props: SelectRootSlotProps<TValue, Multiple>,
  ref: React.ForwardedRef<HTMLButtonElement>
) {
  const { ownerState, ...other } = props;

  return (
    <button type="button" {...other} ref={ref}>
      {other.children}
      <Icon name={enums.Icons.CHEVRON_DOWN} />
    </button>
  );
});

const ListBox = React.forwardRef(function ListBox<
  Value extends Option['value'],
  Multiple extends boolean
>(
  props: SelectListboxSlotProps<Value, Multiple>,
  ref: React.ForwardedRef<HTMLUListElement>
) {
  const { ownerState, ...other } = props;

  return <ul {...other} ref={ref} />;
});

const MUISelect = React.forwardRef(function CustomSelect<
  TValue extends Option['value'],
  Multiple extends boolean
>(
  props: SelectProps<TValue, Multiple>,
  ref: React.ForwardedRef<HTMLButtonElement>
) {
  return (
    <BaseSelect
      ref={ref}
      {...props}
      className={s.base}
      slots={{
        root: Button,
        listbox: ListBox,
        ...props.slots,
      }}
      slotProps={{
        ...props.slotProps,
        listbox: {
          className: cx(s.listbox),
        },
        popup: {
          className: s.popup,
          disablePortal: true,
        },
        root: ({ disabled }) => ({
          className: cx({
            [u.disabled]: disabled,
          }),
        }),
      }}
    />
  );
});
